import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  storyboardResourcesApi,
  type StoryboardDialogueResponse,
  type CreateStoryboardDialoguePayload,
  type UpdateStoryboardDialoguePayload,
  type DialogueSyncItem,
} from '@/services/api/storyboard-resources';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { mapStoryboardDialogueApiItem, type DialogueVoiceApiItem } from '@/lib/dialogue-utils';
import { invalidateStoryboardBoardQueries } from './useStoryboardBoard';
import { invalidateStoryboardReviewStatus } from './storyboardReviewKeys';
import { canvasLayoutKeys } from './useCanvasLayout';

export type StoryboardDialogueType = 1 | 2 | 3;

export interface StoryboardDialogueItem {
  dialogueId: string;
  storyboardId: string;
  characterId?: string;
  /** 角色音色来源标签ID（前端用于编辑音色时定位可写入的装扮记录） */
  characterTagId?: string;
  tagLabel?: string;
  characterName?: string;
  characterImageUrl?: string;
  content: string;
  dialogueType: StoryboardDialogueType;
  sequenceOrder: number;
  startTime?: number;
  duration?: number;
  emotion?: string;
  notes?: string;
  /** 角色标签音色（来自分镜关联的当前角色标签，无则 undefined） */
  voice?: DialogueVoiceApiItem | null;
  createdAt: string;
  updatedAt: string;
}

const mapDialogueResponse = (item: StoryboardDialogueResponse): StoryboardDialogueItem =>
  mapStoryboardDialogueApiItem(item);

export const storyboardDialoguesKeys = {
  all: ['storyboardDialogues'] as const,
  list: (storyboardId: string) => [...storyboardDialoguesKeys.all, storyboardId] as const,
};

export async function invalidateStoryboardDialogueQueries(
  queryClient: QueryClient,
  input: { storyboardId: string; projectId?: string | null }
) {
  clearStoryboardBoardCache(input.projectId ?? undefined);
  if (input.projectId) {
    invalidateStoryboardReviewStatus(queryClient, input.projectId);
  }
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: storyboardDialoguesKeys.list(input.storyboardId) }),
    input.projectId
      ? queryClient.invalidateQueries({
          queryKey: canvasLayoutKeys.context(
            input.projectId,
            'storyboard',
            null,
            null,
            input.storyboardId
          ),
        })
      : Promise.resolve(),
    invalidateStoryboardBoardQueries(queryClient, input.projectId),
  ]);
}

export function useStoryboardDialogues(storyboardId: string | null) {
  return useQuery({
    queryKey: storyboardDialoguesKeys.list(storyboardId!),
    queryFn: async () => {
      const responses = await storyboardResourcesApi.getStoryboardDialogues(storyboardId!);
      return responses.map(mapDialogueResponse).sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    },
    enabled: Boolean(storyboardId),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });
}

export function useCreateStoryboardDialogue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storyboardId,
      payload,
    }: {
      storyboardId: string;
      projectId?: string | null;
      payload: CreateStoryboardDialoguePayload;
    }) => storyboardResourcesApi.createStoryboardDialogue(storyboardId, payload),
    onSuccess: async (_, { storyboardId, projectId }) => {
      await invalidateStoryboardDialogueQueries(queryClient, { storyboardId, projectId });
    },
  });
}

export function useUpdateStoryboardDialogue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogueId,
      payload,
    }: {
      dialogueId: string;
      storyboardId?: string;
      projectId?: string | null;
      payload: UpdateStoryboardDialoguePayload;
    }) => storyboardResourcesApi.updateStoryboardDialogue(dialogueId, payload),
    onSuccess: async (data, variables) => {
      const storyboardId = data.storyboardId ?? variables.storyboardId;
      if (!storyboardId) return;
      await invalidateStoryboardDialogueQueries(queryClient, {
        storyboardId,
        projectId: variables.projectId,
      });
    },
  });
}

export function useDeleteStoryboardDialogue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dialogueId,
      storyboardId: _storyboardId,
    }: {
      dialogueId: string;
      storyboardId: string;
      projectId?: string | null;
    }) => storyboardResourcesApi.deleteStoryboardDialogue(dialogueId),
    onSuccess: async (_, { storyboardId, projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['dialogueVoiceovers', 'storyboard', storyboardId],
      });
      await invalidateStoryboardDialogueQueries(queryClient, { storyboardId, projectId });
    },
  });
}

// ========================================================================
// 缓存操作工具函数（供其他 hooks 调用，避免直接 import storyboardDialoguesKeys）
// ========================================================================

/**
 * 失活指定分镜的对白列表缓存。
 * 在对白排序、激活配音、删除配音等操作后调用。
 */
export function invalidateStoryboardDialogues(
  queryClient: QueryClient,
  storyboardId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: storyboardDialoguesKeys.list(storyboardId),
  });
}

export function removeStoryboardDialoguesCache(
  queryClient: QueryClient,
  storyboardId: string
): void {
  queryClient.removeQueries({ queryKey: storyboardDialoguesKeys.list(storyboardId) });
}

/**
 * 对分镜对白进行重新排序。
 * 成功后自动失活该分镜的对白列表缓存。
 */
export function useReorderStoryboardDialogues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ storyboardId, orderedIds }: { storyboardId: string; orderedIds: string[] }) =>
      storyboardResourcesApi.reorderStoryboardDialogues(storyboardId, orderedIds),
    onSuccess: (_, { storyboardId }) => {
      invalidateStoryboardDialogues(queryClient, storyboardId);
    },
  });
}

/**
 * 全量同步分镜对白。
 * 成功后自动失活该分镜的对白列表缓存及看板缓存。
 */
export function useSyncStoryboardDialogues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      storyboardId,
      payload,
    }: {
      storyboardId: string;
      projectId?: string | null;
      payload: DialogueSyncItem[];
    }) => storyboardResourcesApi.syncStoryboardDialogues(storyboardId, payload),
    onSuccess: async (data, { storyboardId, projectId }) => {
      const sorted = data.dialogues
        .map(mapDialogueResponse)
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder);
      queryClient.setQueryData(storyboardDialoguesKeys.list(storyboardId), sorted);
      await invalidateStoryboardDialogueQueries(queryClient, { storyboardId, projectId });
    },
  });
}
